import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ShopLayout from '../components/shop/ShopLayout';
import ProductCard from '../components/shop/ProductCard';
import api from '../services/api';
import { mapApiProductToCard, resolveImageUrl } from '../utils/shopHelpers';
import './BrandPage.css';
import Seo, { buildItemListSchema } from '../components/seo/Seo';
import { PAGE_SEO, brandSeo } from '../config/seo';

function BrandPage() {
  const { brandSlug } = useParams();
  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await api.getPublicBrandProducts(brandSlug);
        if (!cancelled) {
          setBrand(res?.data?.brand || null);
          setProducts(Array.isArray(res?.data?.products) ? res.data.products : []);
        }
      } catch (e) {
        if (!cancelled) {
          setBrand(null);
          setProducts([]);
          setError(e.message || 'Brand not found');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [brandSlug]);

  const breadcrumbs = brand ? [{ label: brand.name }] : [{ label: 'Brand' }];
  const seo = brand
    ? brandSeo(brand.name, brand.slug)
    : { title: 'Brand | Chesi', path: `/shop/brand/${brandSlug}` };

  return (
    <ShopLayout breadcrumbs={breadcrumbs}>
      <Seo
        {...seo}
        jsonLd={
          products.length ? buildItemListSchema(products, `${brand?.name || 'Brand'} Products`) : null
        }
      />
      <div className="container brand-page">
        {loading && <p className="brand-page__status">Loading…</p>}
        {error && !loading && <p className="brand-page__status brand-page__status--error">{error}</p>}

        {brand && !loading && (
          <header className="brand-page__header">
            {brand.logo_url && (
              <img
                src={resolveImageUrl(brand.logo_url, brand.name)}
                alt={brand.name}
                className="brand-page__logo"
              />
            )}
            <div>
              <h1>{brand.name}</h1>
              {brand.description && <p>{brand.description}</p>}
            </div>
          </header>
        )}

        {products.length > 0 && (
          <div className="brand-page__grid">
            {products.map((p) => (
              <ProductCard key={p.id} product={mapApiProductToCard(p)} />
            ))}
          </div>
        )}

        {brand && !loading && products.length === 0 && (
          <p className="brand-page__empty">No products available for this brand yet.</p>
        )}
      </div>
    </ShopLayout>
  );
}

function BrandsIndexPage() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPublicShopBrands();
        if (!cancelled) setBrands(Array.isArray(res?.data) ? res.data : []);
      } catch {
        if (!cancelled) setBrands([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <ShopLayout breadcrumbs={[{ label: 'Brands' }]}>
      <Seo {...PAGE_SEO.shopBrands} />
      <div className="container brand-page">
        <h1 className="brand-page__title">Shop by Brand</h1>
        {loading && <p className="brand-page__status">Loading brands…</p>}
        <div className="brand-page__index">
          {brands.map((b) => (
            <Link key={b.id} to={`/shop/brand/${b.slug}`} className="brand-page__index-item">
              {b.logo_url ? (
                <img src={resolveImageUrl(b.logo_url, b.name)} alt={b.name} />
              ) : (
                <span className="brand-page__index-name">{b.name}</span>
              )}
            </Link>
          ))}
        </div>
      </div>
    </ShopLayout>
  );
}

export { BrandPage, BrandsIndexPage };
